import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { TaskModel, TaskPriority } from '../../../../03_model/task.model';
import { I18n, NgtButtonComponent, NgtDateInputComponent, NgtInputFieldComponent, NgtOption, NgtSelectDropdownComponent } from '@corporate/ng-tebis-base-components';
import { TaskService } from '../../../../04_service/task.service';
import { AIForm, AIFormState, shouldFormBeBlocked } from '../../../../03_model/ai-chat-message.model';
import { TaskCreatedMessageComponent, TaskCreatedData } from '../task-created-message/task-created-message.component';
import { AssistantService } from '../../../../04_service/assistant/assistant.service';
import { AssistantFormShellComponent } from '../../../assistant/assistant-form-shell/assistant-form-shell.component';

@Component({
  selector: 'tps-create-task',
  imports: [
    AssistantFormShellComponent,
    NgtInputFieldComponent,
    NgtButtonComponent,
    NgtDateInputComponent,
    NgtSelectDropdownComponent
],
  templateUrl: './create-task-message.component.html',
  styleUrl: './create-task-message.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreateTaskMessageComponent implements AIForm {

  i18n = inject(I18n);

  taskService = inject(TaskService);
  assistantService = inject(AssistantService);

  data = input.required<Partial<TaskModel> & AIFormState>();
  update = input.required<(newData: any) => void>();
  submissionState = computed(() => this.data().submissionState);
  blocked = shouldFormBeBlocked(this.submissionState);
  errorMessage = computed(() => this.data().errorMessage || null);

  title = computed(() => this.data().name || '');
  dueDate = computed(() => this.data().dueDate || new Date());
  priority = computed(() => this.data().priority || TaskPriority.mid);
  description = computed(() => this.data().summary || '');

  priorityOptions = computed(() => <NgtOption[]>[
    { key: TaskPriority.low, label: this.i18n.translate('task:priority.low')() },
    { key: TaskPriority.mid, label: this.i18n.translate('task:priority.mid')() },
    { key: TaskPriority.high, label: this.i18n.translate('task:priority.high')() },
  ]);

  constructor() {
    this.i18n.addNamespace('task');
    this.i18n.addNamespace('assistant');
  }

  setTitle(title: string | number | null) {
    if (title === null || typeof title === 'number') {
      title = '';
    }
    this.update()({ name: title });
  }

  setDueDate(dueDate: Date | null | undefined) {
    if (!dueDate) {
      dueDate = new Date();
    }
    this.update()({ dueDate });
  }

  setPriority(priority: string | string[] | null | undefined) {
    if (!priority || Array.isArray(priority) || !Object.values(TaskPriority).includes(priority as TaskPriority)) {
      priority = TaskPriority.mid;
    }
    const prio = priority as TaskPriority;
    this.update()({ priority: prio });
  }

  setDescription(description: string | number | null) {
    if (description === null || typeof description === 'number') {
      description = '';
    }
    this.update()({ summary: description });
  }

  async createTask() {
    if (this.blocked()) {
      return;
    }

    this.update()({ submissionState: 'submitting', errorMessage: null });

    try {
      const task = await this.taskService.createTask({
        name: this.title(),
        dueDate: this.dueDate(),
        priority: this.priority(),
        summary: this.description(),
      });

      await this.assistantService.addMessage({
        type: 'component',
        role: 'user',
        component: TaskCreatedMessageComponent,
        data: <TaskCreatedData>{
          taskID: task.id,
          sentDate: new Date(),
        },
        contentForLLM: `Tool result: Created a new task with the title "${task.name}".`,
        requiresFollowUp: false,
      });

      this.update()({ submissionState: 'submitted', errorMessage: null });
    } catch {
      this.update()({ submissionState: 'idle', errorMessage: this.i18n.translate('assistant:formSubmitFailed')() });
    }
  }
}
